import React from 'react';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Star, Trash2, Inbox as InboxIcon, AlertTriangle, ShieldAlert } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { EmailMessage } from '@shared/types';
interface EmailTableProps {
  emails: EmailMessage[];
  onSelect: (email: EmailMessage) => void;
  onDelete: (id: string) => void;
  isLoading?: boolean;
}
export function EmailTable({ emails, onSelect, onDelete, isLoading }: EmailTableProps) {
  if (isLoading) {
    return (
      <div className="w-full h-64 flex items-center justify-center glass-card rounded-2xl">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="h-12 w-12 bg-white/10 rounded-full" />
          <div className="h-4 w-32 bg-white/10 rounded" />
        </div>
      </div>
    );
  }
  if (emails.length === 0) {
    return (
      <div className="w-full py-20 flex flex-col items-center justify-center glass-card rounded-2xl border-dashed">
        <InboxIcon className="h-16 w-16 text-muted-foreground mb-4 opacity-20" />
        <h3 className="text-xl font-semibold mb-1">Hộp thư của bạn đang trống</h3>
        <p className="text-muted-foreground">Đang chờ email đến...</p>
      </div>
    );
  }
  return (
    <div className="glass-card rounded-2xl overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-white/10 bg-white/5">
              <th className="px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider w-1/4">Người gửi</th>
              <th className="px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider">Chủ đề & Nội dung</th>
              <th className="px-6 py-4 text-xs font-semibold text-muted-foreground uppercase tracking-wider text-right w-32">Đã nhận</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {emails.map((email) => {
              const isSuspicious = (email.phishingScore ?? 0) > 40 || email.isSpam;
              return (
                <tr
                  key={email.id}
                  onClick={() => onSelect(email)}
                  className="group cursor-pointer hover:bg-white/5 transition-colors"
                >
                  <td className="px-6 py-4">
                    <div className="flex flex-col">
                      <span className="font-semibold text-foreground flex items-center gap-2">
                        {email.fromName}
                        {isSuspicious && <AlertTriangle className="h-3.5 w-3.5 text-destructive" />}
                      </span>
                      <span className="text-xs text-muted-foreground truncate max-w-[150px]">{email.fromAddress}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-foreground">{email.subject}</span>
                        {isSuspicious && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Badge variant="destructive" className="px-1 py-0 text-[9px] h-4 uppercase">Rủi ro</Badge>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Email này bị hệ thống đánh dấu là có thể gây nguy hiểm (Phishing/Spam)</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                      </div>
                      <span className="text-sm text-muted-foreground line-clamp-1">{email.preview}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-3">
                      <span className="text-sm text-muted-foreground whitespace-nowrap">
                        {formatDistanceToNow(email.receivedAt, { addSuffix: true, locale: vi })}
                      </span>
                      <div className="opacity-0 group-hover:opacity-100 flex items-center gap-1 transition-opacity">
                        <button className="p-1.5 hover:text-primary transition-colors">
                          <Star className="h-4 w-4" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onDelete(email.id);
                          }}
                          className="p-1.5 hover:text-destructive transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}